import { useEffect, useRef, useState } from 'react';
import { InputNumber, Segmented, Upload } from 'antd';
import { AudioOutlined, CloseOutlined, FileOutlined, InboxOutlined, StopOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { DA_HUY_TAI_LEN } from '../../../lib/api/http';
import { dungLuongVN, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong, maLoi } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { KHOA_LOI_MICRO, KHOA_LOI_TEP } from '../constants';
import { useGhiPhien } from '../hooks/useBienBan';
import { useGhiAm } from '../hooks/useGhiAm';
import { dinhDangThoiLuong, DUOI_NHAN_DUOC, kiemTepGhiAm, tieuDeTuTenTep, TOI_DA_MB } from '../nghiep-vu/bien-ban';
import type { NgonNguPhien, Phien } from '../types';

type Nguon = 'tep' | 'ghi-am';

/** Lỗi về chính tệp mà máy chủ không gắn con trỏ trường, vẫn phải hiện ngay dưới ô tệp. */
const MA_LOI_TEP = new Set(['BIEN_BAN_FILE_TYPE_NOT_SUPPORTED', 'BIEN_BAN_FILE_TOO_LARGE']);
const NHAN_TEP = DUOI_NHAN_DUOC.map((d) => `.${d}`).join(',');

/** Phần ghi âm: giữ riêng để rời thẻ là micro tắt ngay (`useGhiAm` dọn khi gỡ). */
function KhoiGhiAm({ onCoTep, biKhoa }: { onCoTep: (tep: File | null) => void; biKhoa: boolean }) {
  const { t } = useT();
  const g = useGhiAm();
  const dangGhi = g.trangThai === 'dang-ghi';
  const tg = dinhDangThoiLuong(g.giay) ?? '';
  useEffect(() => onCoTep(g.tep), [g.tep]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3 rounded border border-default bg-subtle px-4 py-5">
      {g.lyDoLoi && <LoiTrongHop>{t(KHOA_LOI_MICRO[g.lyDoLoi])}</LoiTrongHop>}
      {dangGhi && (
        <p role="status" className="flex items-center justify-center gap-2 text-sm text-content-primary">
          <span aria-hidden className="size-2.5 animate-pulse rounded-full bg-danger-500" />
          {t('bienBanGhiAm.dangGhi')}
          <span className="font-mono text-lg tabular-nums">{tg}</span>
        </p>
      )}
      {g.trangThai === 'da-ghi' && g.urlNghe && (
        <div className="space-y-2">
          <p role="status" className="text-center text-sm text-content-secondary">{t('bienBanGhiAm.daGhi', { tg })}</p>
          <audio controls src={g.urlNghe} aria-label={t('bienBanGhiAm.nhanNgheLai')} className="w-full" />
        </div>
      )}
      <div className="flex flex-wrap justify-center gap-3">
        {g.trangThai === 'da-ghi' ? (
          <Button variant="outline" size="md" icon={<AudioOutlined />} disabled={biKhoa} onClick={g.lamLai}>
            {t('bienBanGhiAm.lamLai')}
          </Button>
        ) : dangGhi ? (
          <Button variant="destructive" size="md" icon={<StopOutlined />} onClick={g.dung}>
            {t('bienBanGhiAm.dung')}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            icon={<AudioOutlined />}
            loading={g.trangThai === 'xin-quyen'}
            onClick={() => void g.batDau()}
          >
            {g.trangThai === 'xin-quyen' ? t('bienBanGhiAm.dangXinQuyen') : t('bienBanGhiAm.batDau')}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Hộp tải bản ghi âm: chọn tệp hoặc ghi âm ngay, cùng ba trường chung. Gửi xong đóng hộp và mở màn chi tiết để xem tiến
 * trình. Lỗi của từng trường hiện dưới trường đó; đang tải thì Huỷ là ngừng tải, không đóng hộp giữa chừng.
 *
 * Phía gọi gắn `key` mới mỗi lần mở để hộp bắt đầu trống.
 */
export function HopTaiBanGhi({ open, onClose, onDaTao }: { open: boolean; onClose: () => void; onDaTao: (p: Phien) => void }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const { tao } = useGhiPhien();
  const [nguon, setNguon] = useState<Nguon>('tep');
  const [tepChon, setTepChon] = useState<File | null>(null);
  const [tepGhi, setTepGhi] = useState<File | null>(null);
  const [tieuDe, setTieuDe] = useState('');
  const [daSuaTieuDe, setDaSuaTieuDe] = useState(false);
  const [ngonNgu, setNgonNgu] = useState<NgonNguPhien>('auto');
  const [soNguoi, setSoNguoi] = useState<number | null>(null);
  const [daThuGui, setDaThuGui] = useState(false);
  const [tienTrinh, setTienTrinh] = useState(0);
  const huy = useRef<AbortController | null>(null);

  const tep = nguon === 'tep' ? tepChon : tepGhi;
  const loiTep = tep ? kiemTepGhiAm(tep) : null;
  const dangGui = tao.isPending;
  const daHuy = maLoi(tao.error) === DA_HUY_TAI_LEN;
  const loiServer = daHuy ? {} : loiTheoTruong(tao.error);
  const loiTepServer = MA_LOI_TEP.has(maLoi(tao.error) ?? '') ? dichLoi(tao.error) : loiServer['/file'];
  const coLoiChung = Boolean(tao.error) && !daHuy && !loiTepServer && Object.keys(loiServer).length === 0;

  const nhanTep = (f: File | null, tieuDeMacDinh: string) => {
    if (!daSuaTieuDe) setTieuDe(f ? tieuDeMacDinh : '');
  };

  const chonTep = (f: File) => {
    setTepChon(f);
    nhanTep(f, tieuDeTuTenTep(f.name));
  };

  const coTepGhi = (f: File | null) => {
    setTepGhi(f);
    nhanTep(f, t('bienBanTai.tieuDeGhiAm', { luc: ngayGioVN(new Date().toISOString(), locale) }));
  };

  const doiNguon = (v: Nguon) => {
    setNguon(v);
    setTepGhi(null);
    setDaThuGui(false);
    const con = v === 'tep' ? tepChon : null;
    nhanTep(con, con ? tieuDeTuTenTep(con.name) : '');
  };

  const gui = () => {
    setDaThuGui(true);
    if (!tep || loiTep || !tieuDe.trim()) return;
    huy.current = new AbortController();
    setTienTrinh(0);
    tao.mutate(
      {
        vao: { file: tep, title: tieuDe.trim(), language: ngonNgu, ...(soNguoi ? { num_speakers: soNguoi } : {}) },
        onTienTrinh: setTienTrinh,
        signal: huy.current.signal,
      },
      { onSuccess: onDaTao },
    );
  };

  const dongHop = () => {
    if (dangGui) huy.current?.abort();
    else onClose();
  };

  const nhanLoiTep = daThuGui && !tep ? t('bienBanTai.loiChuaChon') : loiTep ? t(KHOA_LOI_TEP[loiTep], { so: soVN(TOI_DA_MB, locale) }) : loiTepServer;

  return (
    <Dialog
      open={open}
      onClose={dongHop}
      title={t('bienBanTai.tieuDe')}
      width={720}
      destroyOnHidden
      mask={{ closable: !dangGui }}
      styles={{ body: { maxHeight: '72vh', overflowY: 'auto' } }}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" size="md" onClick={dongHop}>
            {dangGui ? t('bienBanTai.huyTai') : t('chung.huy')}
          </Button>
          <Button variant="primary" size="md" loading={dangGui} onClick={gui}>
            {dangGui ? t('bienBanTai.dangGui') : t('bienBanTai.gui')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <Segmented<Nguon>
          block
          value={nguon}
          onChange={doiNguon}
          disabled={dangGui}
          options={[
            { value: 'tep', label: t('bienBanTai.theTep') },
            { value: 'ghi-am', label: t('bienBanTai.theGhiAm') },
          ]}
        />

        <Field error={nhanLoiTep}>
          {(p) =>
            nguon === 'tep' ? (
              <div className="space-y-2">
                <Upload.Dragger
                  maxCount={1}
                  showUploadList={false}
                  disabled={dangGui}
                  accept={NHAN_TEP}
                  beforeUpload={(f) => {
                    chonTep(f);
                    return false;
                  }}
                >
                  <div id={p.id} aria-describedby={p['aria-describedby']} className="flex flex-col items-center gap-2 px-4 py-6">
                    <InboxOutlined className="text-2xl text-content-muted" aria-hidden />
                    <p className="text-sm font-medium text-content-primary">{t('bienBanTai.keoTha')}</p>
                    <p className="text-xs text-content-muted">{t('bienBanTai.keoThaMo', { so: soVN(TOI_DA_MB, locale) })}</p>
                  </div>
                </Upload.Dragger>
                {tepChon && (
                  <div className="flex items-center gap-2 rounded border border-default px-3 py-2">
                    <FileOutlined className="shrink-0 text-content-muted" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-content-primary" title={tepChon.name}>{tepChon.name}</p>
                      <p className="text-2xs text-content-muted">{dungLuongVN(tepChon.size, locale)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={dangGui}
                      aria-label={t('bienBanTai.boTep', { ten: tepChon.name })}
                      title={t('bienBanTai.boTep', { ten: tepChon.name })}
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setTepChon(null);
                        nhanTep(null, '');
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <KhoiGhiAm onCoTep={coTepGhi} biKhoa={dangGui} />
            )
          }
        </Field>

        <Field
          label={t('bienBanTai.nhanTieuDe')}
          required
          error={loiServer['/title'] ?? (daThuGui && !tieuDe.trim() ? t('bienBanTai.loiTrongTieuDe') : undefined)}
        >
          {(p) => (
            <Input
              {...p}
              value={tieuDe}
              maxLength={300}
              disabled={dangGui}
              onChange={(e) => {
                setTieuDe(e.target.value);
                setDaSuaTieuDe(true);
              }}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('bienBanTai.nhanNgonNgu')} error={loiServer['/language']}>
            {(p) => (
              <Select<NgonNguPhien>
                {...p}
                className="w-full"
                value={ngonNgu}
                disabled={dangGui}
                onChange={setNgonNgu}
                options={(['auto', 'vi', 'en'] as const).map((v) => ({ value: v, label: t(`bienBanTai.ngonNgu.${v}`) }))}
              />
            )}
          </Field>
          <Field label={t('bienBanTai.nhanSoNguoi')} hint={t('bienBanTai.goiYSoNguoi')} error={loiServer['/num_speakers']}>
            {(p) => (
              <InputNumber
                {...p}
                className="w-full"
                min={1}
                max={50}
                precision={0}
                disabled={dangGui}
                value={soNguoi}
                onChange={(v) => setSoNguoi(v)}
              />
            )}
          </Field>
        </div>

        {dangGui && (
          <div role="status" className="space-y-1.5">
            <p className="text-xs text-content-secondary">{t('bienBanTai.tienTrinh', { so: soVN(tienTrinh, locale) })}</p>
            <div
              role="progressbar"
              aria-label={t('bienBanTai.nhanTienTrinh')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={tienTrinh}
              className="h-1.5 overflow-hidden rounded-full bg-subtle"
            >
              <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${tienTrinh}%` }} />
            </div>
          </div>
        )}

        {coLoiChung && <LoiTrongHop>{dichLoi(tao.error)}</LoiTrongHop>}
      </div>
    </Dialog>
  );
}
